import { extend, override } from 'flarum/common/extend';

/*
 * Forum phone upload review, item 6 (2026-10-31; Forum_Abstract.md, "Phone upload review"). fof/upload inserts each
 * photo into the composer as its raw code, [upl-image-preview uuid=… url=… alt={TEXT?}], and contributors took it for
 * debris and deleted it. fof/rich-text's ProseMirror editor now reads that code as one inline node drawn as the photo:
 * - its markdown-it tokenizer recognises the code (it was plain paragraph text);
 * - the schema has an atom node that keeps the code verbatim (raw) and shows url as an image;
 * - the serializer writes raw back unchanged, so the stored post is byte-identical (SP-20 (Homepage Excerpt) finds
 *   photos by the uuid in the post).
 * The plain-textarea editor (rich text turned off in a user's preferences) is untouched and still shows the code.
 * fof/rich-text is looked up at initializer time; absent, this is a no-op.
 */
const CODE = /^\[upl-image-preview\s[^\]\n]*\]/;

export default function installUploadPreviewNode() {
  const rich = flarum.extensions['fof-rich-text'];
  const markdown = rich?.proseMirror?.markdown;
  const Driver = rich?.proseMirror?.ProseMirrorEditorDriver;
  if (!markdown?.SchemaBuilder || !markdown.MarkdownParserBuilder || !markdown.MarkdownSerializerBuilder || !Driver) return;

  override(markdown.SchemaBuilder.prototype, 'buildNodes', function (original) {
    return original().addBefore('image', 'upl_image_preview', {
      inline: true,
      group: 'inline',
      atom: true,
      draggable: true,
      attrs: { raw: {}, url: { default: '' } },
      // Copy and paste inside the editor keeps the node (and its code).
      parseDOM: [
        {
          tag: 'span.CitRegUplImage[data-raw]',
          getAttrs: (dom) => ({ raw: dom.getAttribute('data-raw'), url: dom.getAttribute('data-url') || '' }),
        },
      ],
      toDOM: (node) => [
        'span',
        { class: 'CitRegUplImage', 'data-raw': node.attrs.raw, 'data-url': node.attrs.url },
        ['img', { src: node.attrs.url, alt: 'Uploaded photo', draggable: 'false' }],
      ],
    });
  });

  extend(markdown.MarkdownParserBuilder.prototype, 'buildTokenizer', function (md) {
    md.inline.ruler.before('link', 'upl_image_preview', (state, silent) => {
      if (state.src.charCodeAt(state.pos) !== 0x5b /* [ */) return false;
      const match = CODE.exec(state.src.slice(state.pos));
      if (!match) return false;
      if (!silent) state.push('upl_image_preview', '', 0).content = match[0];
      state.pos += match[0].length;
      return true;
    });
  });

  extend(markdown.MarkdownParserBuilder.prototype, 'buildTokens', function (tokens) {
    tokens.upl_image_preview = {
      node: 'upl_image_preview',
      getAttrs: (tok) => ({ raw: tok.content, url: imageUrl(tok.content) }),
    };
  });

  extend(markdown.MarkdownSerializerBuilder.prototype, 'buildNodes', function (nodes) {
    nodes.upl_image_preview = (state, node) => state.write(node.attrs.raw);
  });

  // fof/rich-text places the cursor after an insert at start + the inserted text's length, which counts characters of
  // markdown; the editor counts positions in its own document, where the whole code is one position. Unchanged, an
  // upload with text after the cursor put the cursor (and fof's trailing line break) up to ~150 positions too far,
  // inside the following text. For an insert that carries the code, the cursor goes right after the inserted content:
  // where the replaced range ended, shifted by however much the document grew, and back over the block boundary when
  // that lands at the start of the next paragraph (fof then adds its line break there, as it did with the code as text).
  override(Driver.prototype, 'insertBetween', function (original, start, end, text, escape = true) {
    if (escape || !rich.Selection || !/\[upl-image-preview\s/.test(text)) return original(start, end, text, escape);

    const sizeBefore = this.view.state.doc.content.size;
    this.moveCursorTo = () => {
      delete this.moveCursorTo;
      const doc = this.view.state.doc;
      let target = Math.min(end + (doc.content.size - sizeBefore), rich.Selection.atEnd(doc).to);
      if (target > 1 && doc.resolve(target).parentOffset === 0) {
        target = rich.Selection.findFrom(doc.resolve(target - 1), -1, true)?.to ?? target;
      }
      this.moveCursorTo(target);
    };
    try {
      return original(start, end, text, escape);
    } finally {
      delete this.moveCursorTo;
    }
  });
}

function imageUrl(code) {
  const url = /\surl=(\S+?)(?=\s|\]|$)/.exec(code)?.[1] || '';
  return /^(https?:\/\/|\/)/.test(url) ? url : '';
}
